import { Download } from "lucide-react";
import { toast } from "sonner";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { Button } from "@/components/ui/button";
import type { FeaturedAppContent } from "@/lib/featured-apps-data";

/**
 * Hero banner for a featured app page -- image, tagline, and an
 * "Install" button specific to THIS app (not the whole site). The
 * install button reuses the same useInstallPrompt hook the homepage's
 * InstallSection already uses; what makes it install just this one
 * app rather than the whole 230+ tool site is the scoped manifest
 * usePwaManifest already swapped in for this page (see
 * hooks/usePwaManifest.ts) -- this component doesn't need its own
 * install logic, only its own visible entry point for it.
 *
 * Gated on canPrompt (or iOS) -- see InstallToolButton.tsx for the
 * full reasoning: showing this unconditionally and falling back to
 * "look for the install icon in your address bar" pointed at UI that
 * doesn't reliably exist for a nested-scope manifest, which is worse
 * than not showing a button at all. Still keeps a real, separate fix
 * found alongside that reasoning: the iOS branch previously had no
 * onClick at all (`isIOS ? undefined : promptInstall`), meaning the
 * button silently did nothing when tapped on iOS instead of showing
 * the real manual "Add to Home Screen" steps.
 */
export function FeaturedAppHero({ appName, content }: { appName: string; content: FeaturedAppContent }) {
  const { canPrompt, isInstalled, isIOS, promptInstall } = useInstallPrompt();
  const showInstallButton = !isInstalled && (canPrompt || isIOS);

  const handleClick = async () => {
    if (isIOS) {
      toast.info(`Add ${appName} to your Home Screen`, {
        description: 'Tap the Share icon in Safari, then choose "Add to Home Screen."',
        duration: 9000,
      });
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      toast.success(`${appName} installed! Look for its icon.`);
    }
  };

  return (
    <div className="mb-8 rounded-2xl overflow-hidden border border-border">
      <div className="aspect-[21/9] w-full bg-muted overflow-hidden">
        <img src={content.heroImage} alt={`${appName} preview`} className="w-full h-full object-cover" />
      </div>
      <div className="p-5 flex flex-wrap items-center justify-between gap-4 bg-card">
        <p className="text-base font-medium text-foreground/90">{content.heroTagline}</p>
        {showInstallButton && (
          <Button
            variant="outline"
            className="gap-2 shrink-0"
            onClick={handleClick}
            title={isIOS ? "Open the Share menu and choose \"Add to Home Screen\"" : undefined}
          >
            <Download className="h-4 w-4" />
            {isIOS ? "Add to Home Screen" : `Install ${appName}`}
          </Button>
        )}
      </div>
    </div>
  );
}
